import React from 'react';

import classNames from 'classnames';

import { AllocatedFigure, Figure, tdClass, UnallocatedFigure } from './cells';
import ColumnHeading from './ColumnHeading';
import { money, percent, ZERO_DASH } from './formatting';
import LineCell from './LineCell';

import Button from 'CommonComponents/Button';

import ChatIcon from 'Assets/icons/chat-bubble.svg';
import PenIcon from 'Assets/icons/pen-simple.svg';
import PlusIcon from 'Assets/icons/plus.svg';

const ROW_BACKGROUND = ['tw-bg-white', 'tw-bg-background/40', 'tw-bg-background/40', 'tw-bg-background/40'];

/**
 * One allocation cell: value with its % underneath, or "–" / "not allocated". Editors click
 * to edit. A negative project balance (`blocked`) only opens when it holds a stored value,
 * which can then be removed.
 */
const AllocationCell = React.memo(function AllocationCell({ cell, canEdit, blocked, editing, saving, onEdit }) {
    const editable = canEdit && (!blocked || cell.any);
    const blockedHint = blocked ? $t('A project balance below zero cannot be allocated.') : undefined;
    const content = cell.any ? (
        <Figure className={classNames({ 'tw-opacity-50': saving })} value={money(cell.value)} sub={cell.pct === null ? null : percent(cell.pct)} />
    ) : (
        <Figure className={classNames({ 'tw-opacity-50': saving })} value={ZERO_DASH} dash sub={$t('not allocated')} />
    );

    if (!editable) {
        return (
            <td className={tdClass} title={blockedHint}>
                {content}
            </td>
        );
    }

    return (
        <td className={tdClass}>
            <div
                role="button"
                tabIndex={0}
                title={blockedHint ?? $t('Click to edit')}
                onClick={(event) => onEdit(event.currentTarget)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        onEdit(event.currentTarget);
                    }
                }}
                className={classNames(
                    'tw-group/cell tw-relative tw-m-1 tw-cursor-text tw-rounded-md focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-blue',
                    editing ? 'tw-bg-white tw-ring-2 tw-ring-blue' : 'hover:tw-bg-blue-10 hover:tw-ring-1 hover:tw-ring-blue-50',
                )}
            >
                <PenIcon
                    className="tw-absolute tw-start-1.5 tw-top-1/2 tw-h-3 tw-w-3 tw--translate-y-1/2 tw-text-blue tw-opacity-0 group-hover/cell:tw-opacity-100"
                    aria-hidden="true"
                />
                {content}
            </div>
        </td>
    );
});

const AllocateRow = React.memo(function AllocateRow({
    row,
    figures,
    columns,
    open,
    onToggle,
    canEdit,
    editingColumnId,
    savingColumns,
    note,
    onEditCell,
    onNote,
}) {
    return (
        <tr className={classNames('tw-group', ROW_BACKGROUND[row.depth])}>
            <LineCell row={row} open={open} onToggle={onToggle} />
            <td className={tdClass}>
                <Figure value={money(figures.budget)} />
            </td>
            {columns.map((column) => (
                <AllocationCell
                    key={column.id}
                    cell={figures.columns[column.id]}
                    canEdit={canEdit}
                    blocked={!!figures.blocked}
                    editing={editingColumnId === column.id}
                    saving={savingColumns?.has(column.id)}
                    onEdit={(anchor) => onEditCell(row, column, anchor)}
                />
            ))}
            <td className={tdClass}>
                <AllocatedFigure figures={figures} />
            </td>
            <td className={tdClass}>
                <UnallocatedFigure figures={figures} />
            </td>
            <td className={classNames(tdClass, '!tw-text-center')}>
                {row.noteKey && (canEdit || note) && (
                    <button
                        type="button"
                        onClick={() => onNote(row)}
                        title={note || $t('Add a note to this line')}
                        aria-label={note ? $t('Line note') : $t('Add a note to this line')}
                        className={classNames(
                            'tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-md hover:tw-bg-charcoal-15 focus:tw-opacity-100',
                            note ? 'tw-bg-blue-15 tw-text-blue' : 'tw-text-charcoal-50 tw-opacity-0 group-hover:tw-opacity-100',
                        )}
                    >
                        <ChatIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                    </button>
                )}
            </td>
        </tr>
    );
});

/**
 * The Allocate view: one indented Line column, Budget value, one editable column per
 * allocation column, then Allocated, Unallocated and Notes, closed by the Total budget row.
 */
export default function AllocateTable({
    rows,
    figures,
    columns,
    expanded,
    onToggle,
    canEdit,
    editing,
    savingByRow,
    lineNotes,
    onEditCell,
    onNote,
    onColumnNote,
    onRenameColumn,
    onDeleteColumn,
    onAddColumn,
}) {
    const headClass = 'tw-border-b tw-px-3 tw-py-2.5 tw-text-end tw-font-medium tw-text-charcoal tw-whitespace-nowrap';
    const total = figures.total;

    return (
        <div className="tw-overflow-x-auto tw-px-5 tw-pb-5 tw-pt-3">
            <table className="tw-w-full tw-min-w-[980px] tw-border-separate tw-border-spacing-0 tw-text-0.5sm">
                <thead>
                    <tr>
                        <th className={classNames(headClass, '!tw-text-start tw-rounded-ss-lg tw-border-blue-25 tw-bg-blue-10')}>{$t('Line')}</th>
                        <th className={classNames(headClass, 'tw-border-blue-25 tw-bg-blue-10')}>{$t('Budget value')}</th>
                        {columns.map((column) => (
                            <ColumnHeading
                                key={column.id}
                                column={column}
                                canEdit={canEdit}
                                onNote={onColumnNote}
                                onRename={onRenameColumn}
                                onDelete={onDeleteColumn}
                            />
                        ))}
                        <th className={classNames(headClass, 'tw-border-charcoal-25 tw-bg-charcoal-10')}>{$t('Allocated')}</th>
                        <th className={classNames(headClass, 'tw-border-charcoal-25 tw-bg-charcoal-10')}>{$t('Unallocated')}</th>
                        <th className={classNames(headClass, '!tw-text-center tw-w-11 tw-rounded-se-lg tw-border-charcoal-25 tw-bg-charcoal-10')}>
                            {$t('Notes')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {columns.length === 0 && (
                        <tr>
                            <td colSpan={5} className="tw-border-b tw-border-grey tw-px-3 tw-py-6 tw-text-center">
                                <div className="tw-flex tw-flex-col tw-items-center tw-gap-y-3">
                                    <span className="tw-text-sm tw-text-charcoal-75">
                                        {$t('This report has no columns yet. Add a column to start allocating the budget.')}
                                    </span>
                                    {canEdit && (
                                        <Button variant="primary" small onClick={onAddColumn}>
                                            <PlusIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                                            {$t('Add column')}
                                        </Button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    )}
                    {rows.map((row) => (
                        <AllocateRow
                            key={row.key}
                            row={row}
                            figures={figures[row.key]}
                            columns={columns}
                            open={expanded.has(row.key)}
                            onToggle={onToggle}
                            canEdit={canEdit}
                            editingColumnId={editing?.rowKey === row.key ? editing.columnId : null}
                            savingColumns={savingByRow[row.key]}
                            note={row.noteKey ? lineNotes[row.noteKey] : null}
                            onEditCell={onEditCell}
                            onNote={onNote}
                        />
                    ))}
                    <tr className="tw-bg-background tw-font-semibold">
                        <td className="tw-border-t-2 tw-border-charcoal-25 tw-px-3 tw-py-2 tw-text-start">{$t('Total budget')}</td>
                        <td className="tw-border-t-2 tw-border-charcoal-25 tw-p-0 tw-text-end">
                            <Figure value={money(total.budget)} />
                        </td>
                        {columns.map((column) => {
                            const cell = total.columns[column.id];

                            return (
                                <td key={column.id} className="tw-border-t-2 tw-border-charcoal-25 tw-p-0 tw-text-end">
                                    <Figure value={money(cell.value)} sub={cell.pct === null ? null : percent(cell.pct)} />
                                </td>
                            );
                        })}
                        <td className="tw-border-t-2 tw-border-charcoal-25 tw-p-0 tw-text-end">
                            <Figure value={money(total.allocated)} sub={total.totalPct === null ? null : percent(total.totalPct)} />
                        </td>
                        <td className="tw-border-t-2 tw-border-charcoal-25 tw-p-0 tw-text-end">
                            <UnallocatedFigure figures={total} />
                        </td>
                        <td className="tw-border-t-2 tw-border-charcoal-25" />
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
